import { toLocalISOString } from '@/lib'
import { campaignChannel, campaignsShowQueryKey, useCampaignsShow, useCampaignsUpdate } from '@jobilla/nexus-sdk'
import { parseISO } from 'date-fns'
import { useCallback, useMemo } from 'react'
import { centsToEuros, defaultCampaignBudget, eurosToCents } from '../../constants'
import { queryClient } from '../../main'
import { ConformedCampaignReview, useReviewEditor } from '../../ui'

export function useNewCampaignReview(campaignId: string) {
	const campaign = useCampaignsShow(campaignId, {
		staleTime: 1000,
	})

	const campaignData = campaign.data

	// Conform the campaign data to review format - memoized to prevent infinite loops
	const review: ConformedCampaignReview | undefined = useMemo(() => {
		if (!campaignData) { return undefined }

		const startDate = campaignData.startDate ? parseISO(campaignData.startDate) : new Date()
		const endDate = campaignData.endDate
			? parseISO(campaignData.endDate)
			: (() => {
				const end = new Date(startDate)
				end.setDate(end.getDate() + 7)
				return end
			})()

		// Transform custom_fields from array format [{id, value}] to record format {[id]: value}
		const customFieldsArray = campaignData.customFields
		const customFields = customFieldsArray?.reduce<Record<string, string>>((acc, field) => {
			acc[field.id] = field.value
			return acc
		}, {}) ?? {}

		return {
			startDate,
			endDate,
			budget: campaignData.budget ? centsToEuros(campaignData.budget) : centsToEuros(defaultCampaignBudget),
			adCampaignLocations: campaignData.adCampaignLocations ?? [],
			custom_fields: customFields,
			channels: (campaignData.channels as ConformedCampaignReview['channels']) ?? [campaignChannel.META],
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [
		campaignData?.startDate,
		campaignData?.endDate,
		campaignData?.budget,
		campaignData?.adCampaignLocations,
		campaignData?.customFields,
		campaignData?.channels,
	])

	const { mutateAsync: campaignMutationAsync, isPending: isSaving } = useCampaignsUpdate({
		mutationKey: ['update-campaign-review'],
		onMutate: async () => {
			await queryClient.cancelQueries({
				queryKey: campaignsShowQueryKey(campaignId),
			})
		},
		onSettled: () => {
			void queryClient.invalidateQueries({
				queryKey: campaignsShowQueryKey(campaignId),
			})
		},
	})

	const handleReviewChange = useCallback(
		async (value: ConformedCampaignReview) => {
			if (!campaignData) {
				throw new Error('Campaign not found')
			}

			const filledCustomFields = Object.keys(value.custom_fields ?? {})
				.filter((key: string) => value.custom_fields[key]?.trim())
				.map((key: string) => ({ id: key, value: value.custom_fields[key] }))

			await campaignMutationAsync({
				campaign: campaignId,
				data: {
					startDate: toLocalISOString(value.startDate),
					endDate: toLocalISOString(value.endDate),
					budget: eurosToCents(value.budget),
					channels: value.channels,
					// Sent as `undefined` rather than via a conditional spread so the key
					// names stay type-checked: TypeScript does not excess-property-check
					// keys that arrive through a spread, which is how both of these
					// survived the mapper flip still snake_cased. `undefined` is dropped
					// by JSON.stringify, so the wire payload is unchanged when empty.
					adCampaignLocations: value.adCampaignLocations.length > 0
						? value.adCampaignLocations
						: undefined,
					customFields: filledCustomFields.length > 0 ? filledCustomFields : undefined,
				},
			})
		},
		[campaignData, campaignId, campaignMutationAsync],
	)

	useReviewEditor({
		review: review ?? undefined,
		isPending: campaign.isPending,
		isSaving,
		isGenerating: false, // Review data is never "generating"
		onReviewChange: handleReviewChange,
		reviewVersion: campaignData?.updatedAt,
	})

	return {}
}
